import type { CustomerContext } from '../session.js';

/**
 * Human-readable customer-verification status (CCF-2, "Post ID&V Banner:
 * Provide Customer Verification Status").
 *
 * Both the persistent agent banner and the customer dashboard must reflect the
 * customer's CURRENT verification level as the agent progresses through ID&V —
 * not the raw gateway `Y`/`N` flag. The level is tracked server-observed in the
 * customer context's `securityLevel` (`none → pan → medium → high`), raised by
 * each ID&V step (PAN, Medium Security, High Security). CCF-2 §"Step 4 —
 * Security Verification" maps each level to a status label:
 *
 *   | Security level passed | Verification status      |
 *   |-----------------------|--------------------------|
 *   | (card only, no PAN)   | Not Verified             |
 *   | PAN only              | PAN Verified             |
 *   | Medium Security       | Medium Security Verified |
 *   | High Security         | High Security Verified   |
 *
 * #427: when the customer context records WHICH security flow completed the
 * Medium/High verification (`verificationVariant`, e.g. "EDS", "ECL", "Visa",
 * "Business", "Mortgage"), the label names it — e.g. "EDS Medium Security
 * Verified" / "Visa High Security Verified" — so the agent can see which flow
 * the customer passed. #616: the base Medium Security screen (SCRN-03) is the
 * EDS flow, so it records `verificationVariant: 'EDS'` too. When no variant is
 * recorded it falls back to the plain "<Level> Security Verified" text.
 *
 * Returns `undefined` when no customer is in context yet, so callers fall back
 * to their own placeholder (e.g. "N/A" in the banner, "—" on the dashboard)
 * rather than asserting a verification state pre-ID&V.
 *
 * Single source of truth for the verification label — reuse this wherever the
 * status is shown rather than re-deriving it from `securityLevel`.
 */
export function verificationStatusLabel(
  customer: CustomerContext,
): string | undefined {
  const identified = Boolean(
    customer.cif || customer.name || customer.onlineCardNumber,
  );
  if (!identified) return undefined;
  // #427: prepend the completed flow variant (trimmed; ignored when blank) so a
  // stray prefix / double space can never render when no variant applies.
  const variant = customer.verificationVariant?.trim();
  const prefix = variant ? `${variant} ` : '';
  switch (customer.securityLevel) {
    case 'high':
      return `${prefix}High Security Verified`;
    case 'medium':
      return `${prefix}Medium Security Verified`;
    case 'pan':
      return 'PAN Verified';
    case 'none':
    default:
      // Customer identified (card captured) but PAN not yet verified.
      return 'Not Verified';
  }
}
